import { useCallback, useEffect, useState } from 'react';
import {
    BU_EVENT,
    BU_SELECTED_EVENT,
    fetchBusinessUnits,
    getAllBusinessUnits,
    getSelectedBusinessUnit,
    setSelectedBusinessUnit,
} from './businessUnits';

// Menu is mounted on every page (and BuComparison renders it alongside its own
// list), so several consumers hook in at once — share a single in-flight request
// instead of hitting /business-units/mids once per component.
let inFlight = null;

function loadOnce() {
    if (!inFlight) {
        inFlight = fetchBusinessUnits().finally(() => { inFlight = null; });
    }
    return inFlight;
}

export function useBusinessUnits() {
    // Seed from the cache so the switcher renders immediately, then refresh.
    const [units,      setUnits]      = useState(() => getAllBusinessUnits());
    const [selectedBu, setSelectedBu] = useState(() => getSelectedBusinessUnit());
    const [loading,    setLoading]    = useState(true);
    const [error,      setError]      = useState(null);

    useEffect(() => {
        let alive = true;

        const handleUnits = e => {
            if (!alive) return;
            if (e.detail && e.detail.units) setUnits(e.detail.units);
            // The saved id may have pointed at a BU this account no longer has,
            // in which case fetchBusinessUnits() cleared it and the parent wins.
            setSelectedBu(getSelectedBusinessUnit());
        };
        const handleSelected = e => {
            if (!alive) return;
            setSelectedBu(e.detail && e.detail.unit ? e.detail.unit : getSelectedBusinessUnit());
        };

        window.addEventListener(BU_EVENT, handleUnits);
        window.addEventListener(BU_SELECTED_EVENT, handleSelected);

        loadOnce()
            .then(list => {
                if (!alive) return;
                if (list.length) {
                    setUnits(list);
                    setSelectedBu(getSelectedBusinessUnit());
                }
                setError(null);
            })
            .catch(err => {
                if (!alive) return;
                // 401 / deactivated connections are handled by the axios interceptor;
                // anything else just leaves the cached list in place with a message.
                setError(err.response?.data?.message || err.message || 'Request failed');
            })
            .finally(() => { if (alive) setLoading(false); });

        return () => {
            alive = false;
            window.removeEventListener(BU_EVENT, handleUnits);
            window.removeEventListener(BU_SELECTED_EVENT, handleSelected);
        };
    }, []);

    // Persists and broadcasts, so every mounted switcher lands on the same BU.
    const selectBu = useCallback(unit => {
        if (unit && unit.id) setSelectedBusinessUnit(unit.id);
    }, []);

    return { units, loading, error, selectedBu, selectBu };
}
